import { useEffect, useState } from "react";
import { BraintreeContext } from "./BraintreeContext";
import { useService } from "@/hooks/useService";
import braintree, {
  BraintreeError,
  type Client,
  type ThreeDSecure,
} from "braintree-web";
import { trackError } from "@/utils/error-monitoring";
import type { ThreeDSecureVerificationData } from "braintree-web/three-d-secure";

export type BraintreeProviderProps = {
  children: React.ReactNode;
};

export const BraintreeProvider = ({ children }: BraintreeProviderProps) => {
  const [loading, setLoading] = useState(true);
  const [braintreeClient, setBraintreeClient] = useState<Client | null>(null);
  const [threeDSecure, setThreeDSecure] = useState<ThreeDSecure | null>(null);
  const { getPaymentToken } = useService();

  useEffect(() => {
    let isMounted = true;

    const initializeBraintree = async () => {
      try {
        const { token } = await getPaymentToken();
        const client = await braintree.client.create({ authorization: token });
        braintree.threeDSecure.create(
          {
            client,
            version: 2,
          },
          (createError?: BraintreeError, threeDSecure?: ThreeDSecure) => {
            if (createError) {
              trackError(createError as Error);
              return;
            }

            if (!threeDSecure) {
              return;
            }
            threeDSecure.on(
              "lookup-complete",
              (_data?: ThreeDSecureVerificationData, next?: () => void) => {
                next?.();
              },
            );

            if (isMounted) {
              setThreeDSecure(threeDSecure);
            }
          },
        );

        if (isMounted) {
          setBraintreeClient(client);
        }
      } catch (error) {
        trackError(error as Error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initializeBraintree();

    return () => {
      isMounted = false;
    };
  }, [getPaymentToken]);

  const regenerateBraintreeClient = async () => {
    setLoading(true);
    try {
      const { token } = await getPaymentToken();
      const client = await braintree.client.create({ authorization: token });
      setBraintreeClient(client);
      braintree.threeDSecure.create(
        {
          client,
          version: 2,
        },
        (createError?: BraintreeError, threeDSecure?: ThreeDSecure) => {
          if (createError) {
            trackError(createError as Error);
            return;
          }
          if (!threeDSecure) {
            return;
          }
          threeDSecure.on(
            "lookup-complete",
            (_data?: ThreeDSecureVerificationData, next?: () => void) => {
              next?.();
            },
          );
          setThreeDSecure(threeDSecure);
        },
      );
    } catch (error) {
      trackError(error as Error);
    } finally {
      setLoading(false);
    }
  };

  const value = {
    isLoading: loading,
    braintreeClient,
    threeDSecure,
    regenerateClient: regenerateBraintreeClient,
  };

  return <BraintreeContext value={value}>{children}</BraintreeContext>;
};
